import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Plus, Shield, Ban, CheckCircle, Mail, Copy, Loader2, Edit2, KeyRound, AlertTriangle,
  Archive, RotateCcw,
} from 'lucide-react';
import ReactivateAccountDialog from '../components/ReactivateAccountDialog';

const ROLE_STYLE = {
  ADMIN: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  SNC: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  PHYSIO: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  COACH: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  ATHLETE: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
};

const STATUS_STYLE = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-400',
  INVITED: 'bg-amber-500/15 text-amber-400',
  DISABLED: 'bg-rose-500/15 text-rose-400',
};

export default function UserManagementPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;

  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('users');

  // Invite dialog
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'COACH', first_name: '', last_name: '', team_ids: [] });
  const [inviteToken, setInviteToken] = useState('');
  const [inviteEmailSent, setInviteEmailSent] = useState(false);
  const [inviting, setInviting] = useState(false);

  // Edit dialog
  const [editUser, setEditUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editTeamIds, setEditTeamIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Reactivation dialog (iter_122)
  const [reactivateUser, setReactivateUser] = useState(null);

  // Archive confirmation (iter_122)
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, iRes, tRes] = await Promise.all([
        api.get('/users'),
        api.get('/users/invitations'),
        api.get('/teams'),
      ]);
      setUsers(uRes.data);
      setInvitations(iRes.data);
      setTeams(tRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async () => {
    // Athletes MUST have at least one team selected (server enforces this too).
    if (inviteData.role === 'ATHLETE' && (!inviteData.team_ids || inviteData.team_ids.length === 0)) {
      toast.error(t('Select at least one team for the athlete.', 'Seleziona almeno una squadra per l\'atleta.'));
      return;
    }
    setInviting(true);
    try {
      const r = await api.post('/users/invite', { ...inviteData, frontend_origin: window.location.origin });
      setInviteToken(r.data.token);
      setInviteEmailSent(r.data.email_sent === true);
      if (r.data.email_sent) {
        toast.success(t('Invitation email sent!', 'Email di invito inviata!'));
      } else {
        toast.warning(t('Invitation created but email failed. Share the link manually.', 'Invito creato ma email non inviata. Condividi il link manualmente.'));
      }
      fetchData();
    } catch (err) {
      // iter_122: archived-account reactivation flow
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && detail.error === 'ARCHIVED_ACCOUNT_FOUND') {
        setShowInvite(false);
        setReactivateUser(detail.archived_user);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error'));
      }
    } finally {
      setInviting(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await api.put(`/users/${archiveTarget.id}/status`, { status: 'DISABLED' });
      toast.success(t('User archived', 'Utente archiviato'));
      setArchiveTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setArchiving(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    // Kept for backward compatibility (used only for re-enable path — archive uses handleArchive)
    const newStatus = currentStatus === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
    try {
      await api.put(`/users/${userId}/status`, { status: newStatus });
      toast.success(newStatus === 'DISABLED' ? t('User archived', 'Utente archiviato') : t('User enabled', 'Utente abilitato'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      if (editRole !== editUser.role) {
        await api.put(`/users/${editUser.id}/role`, { role: editRole });
      }
      if (editTeamIds.length > 0) {
        await api.put(`/users/${editUser.id}/teams`, { team_ids: editTeamIds, primary_team_id: editTeamIds[0] });
      }
      toast.success(t('User updated', 'Utente aggiornato'));
      setEditUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await api.post(`/users/invitations/${inviteId}/cancel`);
      toast.success(t('Invitation cancelled', 'Invito annullato'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const r = await api.post(`/users/invitations/${inviteId}/resend`, { frontend_origin: window.location.origin });
      if (r.data.email_sent) {
        toast.success(t('Invitation email resent!', 'Email di invito rinviata!'));
      } else {
        toast.warning(t('New token generated but email failed', 'Nuovo token generato ma email non inviata'));
      }
      setInviteToken(r.data.token);
      setInviteEmailSent(r.data.email_sent === true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditRole(u.role);
    setEditTeamIds((u.teams || []).map(t => t.team_id));
  };

  const pendingInvites = invitations.filter(i => i.status === 'PENDING');
  const activeUsers = users.filter(u => u.is_active !== false);
  const archivedUsers = users.filter(u => u.is_active === false);

  const [archivedSearch, setArchivedSearch] = useState('');
  const archivedFiltered = archivedUsers.filter(u => {
    if (!archivedSearch) return true;
    const q = archivedSearch.toLowerCase();
    return (
      (u.first_name || '').toLowerCase().includes(q) ||
      (u.last_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Users className="w-6 h-6 text-cyan-400" />
            {t('User Management', 'Gestione Utenti')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{t('Manage users, invitations, and access', 'Gestisci utenti, inviti e accessi')}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden" data-testid="tab-toggle">
            <button onClick={() => setTab('users')} className={`px-4 py-2 text-sm font-medium ${tab === 'users' ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500'}`} data-testid="tab-users">{t('Users', 'Utenti')} ({activeUsers.length})</button>
            <button onClick={() => setTab('invites')} className={`px-4 py-2 text-sm font-medium ${tab === 'invites' ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500'}`} data-testid="tab-invites">{t('Invitations', 'Inviti')} ({pendingInvites.length})</button>
            <button onClick={() => setTab('archived')} className={`px-4 py-2 text-sm font-medium ${tab === 'archived' ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500'}`} data-testid="tab-archived">{t('Archived', 'Archiviati')} ({archivedUsers.length})</button>
          </div>
          <Button onClick={() => { setShowInvite(true); setInviteToken(''); setInviteEmailSent(false); setInviteData({ email: '', role: 'COACH', first_name: '', last_name: '', team_ids: [] }); }} data-testid="invite-btn">
            <Plus className="w-4 h-4 mr-2" /> {t('Invite User', 'Invita Utente')}
          </Button>
        </div>
      </div>

      {/* Users Table */}
      {tab === 'users' && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="users-table-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500 text-xs uppercase tracking-wider">
                    <th className="px-4 py-3 text-left">{t('Name', 'Nome')}</th>
                    <th className="px-4 py-3 text-left">{t('Email', 'Email')}</th>
                    <th className="px-4 py-3 text-left">{t('Role', 'Ruolo')}</th>
                    <th className="px-4 py-3 text-left">{t('Teams', 'Squadre')}</th>
                    <th className="px-4 py-3 text-left">{t('Status', 'Stato')}</th>
                    <th className="px-4 py-3 text-left">{t('Last Login', 'Ultimo Accesso')}</th>
                    <th className="px-4 py-3 text-right">{t('Actions', 'Azioni')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/50">
                  {activeUsers.map(u => (
                    <tr key={u.id} className="hover:bg-zinc-800/30 transition-colors" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3 text-zinc-200 font-medium">{u.first_name} {u.last_name}</td>
                      <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                      <td className="px-4 py-3"><Badge className={`text-[10px] ${ROLE_STYLE[u.role] || ''}`}>{u.role}</Badge></td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(u.teams || []).map((tm, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded">{tm.team_code || tm.team_id?.slice(0, 6)}</span>
                          ))}
                          {(!u.teams || u.teams.length === 0) && <span className="text-zinc-600 text-xs">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3"><Badge className={`text-[10px] ${STATUS_STYLE[u.status] || 'bg-zinc-700 text-zinc-400'}`}>{u.status || 'ACTIVE'}</Badge></td>
                      <td className="px-4 py-3 text-zinc-500 text-xs">{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(u)} className="p-1.5 hover:bg-zinc-700 rounded transition-colors" title={t('Edit', 'Modifica')} data-testid={`edit-user-${u.id}`}>
                            <Edit2 className="w-3.5 h-3.5 text-zinc-400" />
                          </button>
                          <button onClick={() => setArchiveTarget(u)} className="p-1.5 hover:bg-zinc-700 rounded transition-colors" title={t('Archive', 'Archivia')} data-testid={`archive-user-${u.id}`}>
                            <Archive className="w-3.5 h-3.5 text-rose-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invitations Tab */}
      {tab === 'invites' && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="invitations-card">
          <CardContent className="p-0">
            {invitations.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">{t('No invitations yet.', 'Nessun invito.')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 text-zinc-500 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 text-left">{t('Email', 'Email')}</th>
                      <th className="px-4 py-3 text-left">{t('Name', 'Nome')}</th>
                      <th className="px-4 py-3 text-left">{t('Role', 'Ruolo')}</th>
                      <th className="px-4 py-3 text-left">{t('Status', 'Stato')}</th>
                      <th className="px-4 py-3 text-left">{t('Created', 'Creato')}</th>
                      <th className="px-4 py-3 text-right">{t('Actions', 'Azioni')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/50">
                    {invitations.map(inv => (
                      <tr key={inv.id} className="hover:bg-zinc-800/30" data-testid={`invite-row-${inv.id}`}>
                        <td className="px-4 py-3 text-zinc-300">{inv.email}</td>
                        <td className="px-4 py-3 text-zinc-400">{inv.first_name} {inv.last_name}</td>
                        <td className="px-4 py-3"><Badge className={`text-[10px] ${ROLE_STYLE[inv.role] || ''}`}>{inv.role}</Badge></td>
                        <td className="px-4 py-3"><Badge className={`text-[10px] ${inv.status === 'PENDING' ? 'bg-amber-500/15 text-amber-400' : inv.status === 'ACCEPTED' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>{inv.status}</Badge></td>
                        <td className="px-4 py-3 text-zinc-500 text-xs">{new Date(inv.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right">
                          {inv.status === 'PENDING' && (
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => handleResendInvite(inv.id)} className="p-1.5 hover:bg-zinc-700 rounded" title="Resend" data-testid={`resend-${inv.id}`}>
                                <Mail className="w-3.5 h-3.5 text-amber-400" />
                              </button>
                              <button onClick={() => handleCancelInvite(inv.id)} className="p-1.5 hover:bg-zinc-700 rounded" title="Cancel" data-testid={`cancel-${inv.id}`}>
                                <Ban className="w-3.5 h-3.5 text-rose-400" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Archived Users Tab (iter_122) */}
      {tab === 'archived' && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="archived-card">
          <CardContent className="p-4">
            <div className="mb-3">
              <Input
                placeholder={t('Search archived by name or email…', 'Cerca archiviati per nome o email…')}
                value={archivedSearch}
                onChange={e => setArchivedSearch(e.target.value)}
                className="bg-zinc-950 border-zinc-800 max-w-md"
                data-testid="archived-search"
              />
            </div>
            {archivedFiltered.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm" data-testid="archived-empty">
                {t('No archived users.', 'Nessun utente archiviato.')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 text-zinc-500 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 text-left">{t('Name', 'Nome')}</th>
                      <th className="px-4 py-3 text-left">{t('Email', 'Email')}</th>
                      <th className="px-4 py-3 text-left">{t('Role', 'Ruolo')}</th>
                      <th className="px-4 py-3 text-left">{t('Last Team', 'Ultima Squadra')}</th>
                      <th className="px-4 py-3 text-left">{t('Archived', 'Archiviato')}</th>
                      <th className="px-4 py-3 text-right">{t('Actions', 'Azioni')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/50">
                    {archivedFiltered.map(u => {
                      const lastTeamRow = (u.teams || [])[0];
                      const lastTeam = lastTeamRow ? (lastTeamRow.team_code || lastTeamRow.team_name) : '—';
                      const archivedOn = u.archived_at ? new Date(u.archived_at).toLocaleDateString() : (u.updated_at ? new Date(u.updated_at).toLocaleDateString() : '—');
                      return (
                        <tr key={u.id} className="hover:bg-zinc-800/30 transition-colors" data-testid={`archived-row-${u.id}`}>
                          <td className="px-4 py-3 text-zinc-200 font-medium">{u.first_name} {u.last_name}</td>
                          <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                          <td className="px-4 py-3"><Badge className={`text-[10px] ${ROLE_STYLE[u.role] || ''}`}>{u.role}</Badge></td>
                          <td className="px-4 py-3 text-zinc-400 text-xs">{lastTeam}</td>
                          <td className="px-4 py-3 text-zinc-500 text-xs">{archivedOn}</td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                              onClick={() => setReactivateUser({
                                id: u.id,
                                first_name: u.first_name,
                                last_name: u.last_name,
                                role: u.role,
                                last_team: lastTeam,
                                archived_at: u.archived_at || u.updated_at,
                              })}
                              data-testid={`reactivate-user-${u.id}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                              {t('Reactivate', 'Riattiva')}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{inviteToken ? t('Invitation Created', 'Invito Creato') : t('Invite User', 'Invita Utente')}</DialogTitle>
          </DialogHeader>
          {inviteToken ? (
            <div className="space-y-4">
              {inviteEmailSent ? (
                <div className="flex items-center gap-2 p-3 rounded bg-emerald-500/10 border border-emerald-500/20" data-testid="invite-email-sent">
                  <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-emerald-300">{t('Invitation email sent', 'Email di invito inviata')}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">{inviteData.email}</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded bg-amber-500/10 border border-amber-500/20" data-testid="invite-email-failed">
                  <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-amber-300">{t('Email could not be sent', 'Impossibile inviare email')}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">{t('Share the link manually below', 'Condividi il link manualmente')}</div>
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-zinc-500 mb-1">{t('Invitation link (backup)', 'Link di invito (backup)')}</div>
                <div className="flex items-center gap-2">
                  <Input value={`${window.location.origin}/accept-invite?token=${inviteToken}`} readOnly className="bg-zinc-950 border-zinc-800 text-xs" data-testid="invite-link" />
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/accept-invite?token=${inviteToken}`); toast.success('Copied!'); }} data-testid="copy-link-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Button onClick={() => setShowInvite(false)} className="w-full">{t('Done', 'Fatto')}</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-zinc-400">{t('First Name', 'Nome')}</Label><Input value={inviteData.first_name} onChange={e => setInviteData(d => ({...d, first_name: e.target.value}))} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="invite-first-name" /></div>
                <div><Label className="text-zinc-400">{t('Last Name', 'Cognome')}</Label><Input value={inviteData.last_name} onChange={e => setInviteData(d => ({...d, last_name: e.target.value}))} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="invite-last-name" /></div>
              </div>
              <div><Label className="text-zinc-400">Email</Label><Input type="email" value={inviteData.email} onChange={e => setInviteData(d => ({...d, email: e.target.value}))} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="invite-email" /></div>
              <div>
                <Label className="text-zinc-400">{t('Role', 'Ruolo')}</Label>
                <Select value={inviteData.role} onValueChange={v => setInviteData(d => ({...d, role: v}))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {['ADMIN', 'SNC', 'PHYSIO', 'COACH', 'ATHLETE'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">
                  {t('Team(s)', 'Squadra/e')}
                  {inviteData.role === 'ATHLETE' && (
                    <span className="ml-1.5 text-rose-400 text-xs font-medium">{t('* required for athletes', '* obbligatorio per gli atleti')}</span>
                  )}
                </Label>
                <div className={`mt-1 space-y-1 max-h-32 overflow-y-auto ${inviteData.role === 'ATHLETE' && inviteData.team_ids.length === 0 ? 'border border-rose-500/40 rounded-sm p-1' : ''}`}>
                  {teams.length === 0 && (
                    <p className="text-xs text-zinc-500 p-2">{t('No teams available. Create a team first.', 'Nessuna squadra disponibile. Crea prima una squadra.')}</p>
                  )}
                  {teams.map(tm => (
                    <label key={tm.id} className="flex items-center gap-2 p-1.5 hover:bg-zinc-800/50 rounded cursor-pointer">
                      <input type="checkbox" checked={inviteData.team_ids.includes(tm.id)}
                        onChange={e => {
                          setInviteData(d => ({
                            ...d,
                            team_ids: e.target.checked ? [...d.team_ids, tm.id] : d.team_ids.filter(id => id !== tm.id)
                          }));
                        }}
                        className="rounded border-zinc-700" data-testid={`invite-team-${tm.id}`} />
                      <span className="text-sm text-zinc-300">{tm.code} — {tm.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleInvite}
                  disabled={
                    inviting ||
                    !inviteData.email ||
                    !inviteData.first_name ||
                    (inviteData.role === 'ATHLETE' && inviteData.team_ids.length === 0)
                  }
                  data-testid="send-invite-btn"
                >
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  {t('Send Invitation', 'Invia Invito')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{t('Edit User', 'Modifica Utente')}</DialogTitle>
            <DialogDescription className="text-zinc-400">{editUser?.first_name} {editUser?.last_name} ({editUser?.email})</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400">{t('Role', 'Ruolo')}</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="edit-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {['ADMIN', 'SNC', 'PHYSIO', 'COACH', 'ATHLETE'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400">{t('Teams', 'Squadre')}</Label>
              <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                {teams.map(tm => (
                  <label key={tm.id} className="flex items-center gap-2 p-1.5 hover:bg-zinc-800/50 rounded cursor-pointer">
                    <input type="checkbox" checked={editTeamIds.includes(tm.id)}
                      onChange={e => setEditTeamIds(prev => e.target.checked ? [...prev, tm.id] : prev.filter(id => id !== tm.id))}
                      className="rounded border-zinc-700" data-testid={`edit-team-${tm.id}`} />
                    <span className="text-sm text-zinc-300">{tm.code} — {tm.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="border-zinc-700">{t('Cancel', 'Annulla')}</Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {t('Save Changes', 'Salva')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Archive Confirmation Dialog (iter_122) */}
      <Dialog open={!!archiveTarget} onOpenChange={() => { if (!archiving) setArchiveTarget(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="archive-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Archive className="w-5 h-5 text-rose-400" />
              {t('Archive this account?', 'Archiviare questo account?')}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 leading-relaxed">
              {t(
                "The user will no longer be able to sign in and will be removed from the active lists. All historical data will be preserved and the account can be reactivated later.",
                "L'utente non potrà più accedere e verrà rimosso dagli elenchi attivi. Tutti i dati storici saranno conservati e l'account potrà essere riattivato in futuro."
              )}
            </DialogDescription>
          </DialogHeader>
          {archiveTarget && (
            <div className="rounded border border-zinc-800 bg-zinc-950/70 p-3 text-sm">
              <div className="text-zinc-200 font-medium">{archiveTarget.first_name} {archiveTarget.last_name}</div>
              <div className="text-zinc-500 text-xs">{archiveTarget.email} · {archiveTarget.role}</div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-zinc-700" onClick={() => setArchiveTarget(null)} disabled={archiving} data-testid="archive-cancel-btn">
              {t('Cancel', 'Annulla')}
            </Button>
            <Button onClick={handleArchive} disabled={archiving} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="archive-confirm-btn">
              {archiving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
              {t('Archive', 'Archivia')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Account Dialog (iter_122) */}
      <ReactivateAccountDialog
        open={!!reactivateUser}
        onClose={() => setReactivateUser(null)}
        archivedUser={reactivateUser}
        teams={teams}
        api={api}
        lang={language}
        onReactivated={() => { fetchData(); }}
      />
    </div>
  );
}
